import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";

const nav = [
  {
    label: "Solutions",
    children: [
      { label: "Wholesale Voice Termination", to: "/solutions/wholesale-voice-termination", icon: "ri-broadcast-line" },
      { label: "For VoIP Operators", to: "/solutions/voip-operators", icon: "ri-server-line" },
      { label: "For Resellers", to: "/solutions/resellers", icon: "ri-store-2-line" },
      { label: "For Call Centers & CPaaS", to: "/solutions/call-centers-cpaas", icon: "ri-headphone-line" },
      { label: "Anti-fraud & Quality", to: "/solutions/anti-fraud", icon: "ri-shield-check-line" },
    ],
  },
  { label: "Coverage & Rates", to: "/coverage" },
  { label: "Network & Quality", to: "/network" },
  { label: "Why Voxmaster", to: "/why-voxmaster" },
  { label: "Company", to: "/company" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-background-50"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center cursor-pointer">
          <Logo className="h-12 md:h-[60px]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((item) =>
            "children" in item ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setSolutionsOpen(true)}
                onMouseLeave={() => setSolutionsOpen(false)}
              >
                <button
                  type="button"
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-md flex items-center gap-1 whitespace-nowrap cursor-pointer"
                >
                  {item.label}
                  <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center" />
                </button>
                {solutionsOpen && (
                  <div className="absolute top-full left-0 w-80 pt-2">
                    <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                      {item.children!.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="flex items-start gap-3 px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                            <i className={`${c.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                          </span>
                          <div>
                            <p className="text-sm font-medium text-foreground-950">{c.label}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to!}
                className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${
                  location.pathname === item.to
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/coverage"
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 px-3 py-2 whitespace-nowrap cursor-pointer"
          >
            Request rates
          </Link>
          <Link
            to="/contact"
            className="text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            Contact Us
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl w-6 h-6 flex items-center justify-center`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {nav.map((item) =>
              "children" in item ? (
                <div key={item.label} className="py-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 px-3 py-2">
                    {item.label}
                  </p>
                  {item.children!.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-800 hover:text-primary-600 cursor-pointer"
                    >
                      <i className={`${c.icon} w-4 h-4 flex items-center justify-center`} />
                      {c.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to!}
                  className="px-3 py-2 text-sm font-medium text-foreground-800 hover:text-primary-600 cursor-pointer"
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="border-t border-background-200 mt-2 pt-3 flex flex-col gap-2">
              <Link
                to="/coverage"
                className="text-sm font-medium text-foreground-800 px-3 py-2 cursor-pointer"
              >
                Request rates
              </Link>
              <Link
                to="/contact"
                className="text-sm font-semibold bg-primary-500 text-background-50 px-4 py-2.5 rounded-md text-center whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}